/**
 * @file time-picker/subcomponents/clock-dial.tsx
 *
 * Clock dial SVG — clock face with pointer interaction.
 *
 * Renders SVG circle with:
 *   - Background circle (SurfaceContainerHighest)
 *   - 12 (or 24) numbers positioned evenly on circle
 *   - Selector: center dot + track line + handle circle (all Primary)
 *   - Pointer events: click + drag to pick hour/minute
 *   - autoSwitchToMinute: automatically switches to minute after hour selection
 *
 * Math ported from `AnalogTimePickerState` in TimePicker.kt:
 *   - RadiansPerHour, RadiansPerMinute, InnerCircleToSizeRatio, OuterCircleToSizeRatio
 *   - `rotateTo`, `onTap` logic
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePicker.kt (ClockFace, AnalogTimePickerState)
 */

import * as React from "react";
import { cn } from "../../../../lib/utils";
import { TP_COLORS, TP_SIZE } from "../time-picker.tokens";
import type {
	TimePickerSelectionMode,
	TimePickerState,
} from "../time-picker.types";

// ─── Constants (from TimePicker.kt) ──────────────────────────────────────────────

const FULL_CIRCLE = Math.PI * 2;
const QUARTER_CIRCLE = Math.PI / 2;
const RADIANS_PER_HOUR = FULL_CIRCLE / 12;
const RADIANS_PER_MINUTE = FULL_CIRCLE / 60;

/**
 * OuterCircleToSizeRatio — 101dp on 256dp dial.
 */
const OUTER_CIRCLE_RATIO = 101 / TP_SIZE.clockDialSize;

/**
 * InnerCircleToSizeRatio — 69dp on 256dp dial.
 */
const INNER_CIRCLE_RATIO = 69 / TP_SIZE.clockDialSize;

/** SVG Center = diameter / 2 */
const CENTER = TP_SIZE.clockDialSize / 2; // 128

/** Outer ring radius (hours 1-12, minutes) */
const OUTER_RADIUS = TP_SIZE.clockDialSize * OUTER_CIRCLE_RATIO; // ~106.8

/** Inner ring radius (hours 13-23 in 24h mode) */
const INNER_RADIUS = TP_SIZE.clockDialSize * INNER_CIRCLE_RATIO; // ~74.9

/** Handle circle radius = selectorHandleSize / 2 */
const HANDLE_RADIUS = TP_SIZE.selectorHandleSize / 2; // 24

/**
 * SELECTOR_GAP — minimum gap from handle edge to container edge.
 * MD3 spec: 4dp gap between selector handle and background circle.
 */
const SELECTOR_GAP = 4;

// ─── Number data ────────────────────────────────────────────────────────────────

interface ClockNumber {
	value: number;
	x: number;
	y: number;
	radius: number; // for 24h inner ring detection
}

/**
 * Calculates number position on dial circle.
 * Starting from 12 o'clock (top) = -PI/2
 */
const getPositionOnDial = (
	index: number,
	total: number,
	radius: number,
): { x: number; y: number } => {
	const angle = (index / total) * FULL_CIRCLE - QUARTER_CIRCLE;
	return {
		x: CENTER + radius * Math.cos(angle),
		y: CENTER + radius * Math.sin(angle),
	};
};

/** Generates number list for 12h hour mode (1-12) */
const buildHour12Numbers = (): ClockNumber[] =>
	Array.from({ length: 12 }, (_, i) => {
		const hour = i === 0 ? 12 : i;
		const pos = getPositionOnDial(i, 12, OUTER_RADIUS);
		return { value: hour, ...pos, radius: OUTER_RADIUS };
	});

/** Generates number list for 24h mode (0-23, outer 0-11, inner 12-23) */
const buildHour24Numbers = (): ClockNumber[] => {
	const outer: ClockNumber[] = Array.from({ length: 12 }, (_, i) => {
		const hour = i; // 0-11
		const pos = getPositionOnDial(i, 12, OUTER_RADIUS);
		return { value: hour, ...pos, radius: OUTER_RADIUS };
	});
	const inner: ClockNumber[] = Array.from({ length: 12 }, (_, i) => {
		const hour = i + 12; // 12-23
		const pos = getPositionOnDial(i, 12, INNER_RADIUS);
		return { value: hour, ...pos, radius: INNER_RADIUS };
	});
	return [...outer, ...inner];
};

/** Generates number list for minute mode (0, 5, 10, ..., 55) */
const buildMinuteNumbers = (): ClockNumber[] =>
	Array.from({ length: 12 }, (_, i) => {
		const minute = i * 5; // 0, 5, 10, ..., 55
		const pos = getPositionOnDial(i, 12, OUTER_RADIUS);
		return { value: minute, ...pos, radius: OUTER_RADIUS };
	});

// ─── Math ──────────────────────────────────────────────────────────────────────

/**
 * Calculates angle from pointer position (x, y) relative to center.
 * Similar to `atan2` in Compose — returns angle in [-PI, PI].
 * Offset by -QUARTER_CIRCLE so 12 o'clock = 0 rad.
 */
const getAngleFromPointer = (x: number, y: number): number => {
	return Math.atan2(y - CENTER, x - CENTER);
};

/**
 * Normalizes angle to range [0, 2PI).
 */
const normalizeAngle = (angle: number): number => {
	let a = angle % FULL_CIRCLE;
	if (a < 0) a += FULL_CIRCLE;
	return a;
};

/**
 * Converts angle → hour (12h mode, 1-12).
 * Ported from `Float.toHour()` in AnalogTimePickerState.
 */
const angleToHour12 = (angle: number): number => {
	const hourOffset = RADIANS_PER_HOUR / 2;
	const totalOffset = hourOffset + QUARTER_CIRCLE;
	const hour = Math.round((angle + totalOffset) / RADIANS_PER_HOUR) % 12;
	return hour === 0 ? 12 : hour;
};

/**
 * Converts angle → minute (0-55, step 5 on click, step 1 on drag).
 * Ported from `Float.toMinute()` in AnalogTimePickerState.
 */
const angleToMinute = (angle: number, snap = true): number => {
	if (snap) {
		const minuteOffset = RADIANS_PER_MINUTE / 2;
		const totalOffset = minuteOffset + QUARTER_CIRCLE;
		const raw = Math.round((angle + totalOffset) / RADIANS_PER_MINUTE) % 60;
		return raw < 0 ? raw + 60 : raw;
	}
	// Drag mode — step 1
	const normalized = normalizeAngle(angle + QUARTER_CIRCLE);
	const raw = Math.round((normalized / FULL_CIRCLE) * 60) % 60;
	return raw < 0 ? raw + 60 : raw;
};

/**
 * Calculates selector handle position from angle and is24h/isPm.
 * Ported from `AnalogTimePickerState.selectorPos`.
 */
const getSelectorPosition = (
	angle: number,
	selection: TimePickerSelectionMode,
	is24hour: boolean,
	isPm: boolean,
): { x: number; y: number } => {
	// Inner circle for 24h PM hours (13-23)
	const useInner = is24hour && isPm && selection === "hour";
	const baseRadius = useInner ? INNER_RADIUS : OUTER_RADIUS;

	// MD3: clamps handle center so handle edge maintains SELECTOR_GAP to container edge.
	// maxLength = CENTER - HANDLE_RADIUS - GAP = 128 - 24 - 4 = 100px
	const length = Math.min(baseRadius, CENTER - HANDLE_RADIUS - SELECTOR_GAP);

	return {
		x: CENTER + length * Math.cos(angle),
		y: CENTER + length * Math.sin(angle),
	};
};

/**
 * Calculates display angle from hour/minute value.
 * Ported from `hourAngle = RadiansPerHour * (state.hour % 12) - FullCircle / 4`
 */
const valueToAngle = (
	value: number,
	selection: TimePickerSelectionMode,
): number => {
	if (selection === "hour") {
		return RADIANS_PER_HOUR * (value % 12) - QUARTER_CIRCLE;
	}
	return RADIANS_PER_MINUTE * value - QUARTER_CIRCLE;
};

// ─── Props ─────────────────────────────────────────────────────────────────────

interface ClockDialProps {
	/** Hoisted state */
	state: TimePickerState;
	/**
	 * Automatically switches selection to "minute" after picking hour.
	 * @default true
	 */
	autoSwitchToMinute?: boolean;
	/** Inline style override */
	style?: React.CSSProperties;
	className?: string;
}

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * ClockDial — SVG clock face with pointer interaction.
 *
 * Renders numbers around dial circle and selector handle, supporting:
 * - Click to select specific hour/minute
 * - Drag for continuous rotation
 * - autoSwitchToMinute
 * - 24h mode (dual ring: outer 0-11, inner 12-23)
 *
 * @example
 * ```tsx
 * <ClockDial state={state} autoSwitchToMinute />
 * ```
 */
export const ClockDial: React.FC<ClockDialProps> = ({
	state,
	autoSwitchToMinute = true,
	style,
	className,
}) => {
	const {
		selection,
		is24hour,
		isPm,
		hour,
		minute,
		setHour,
		setMinute,
		setSelection,
	} = state;

	// Calculate display angle from current value
	const displayAngle = React.useMemo(() => {
		if (selection === "hour") {
			return valueToAngle(hour, "hour");
		}
		return valueToAngle(minute, "minute");
	}, [selection, hour, minute]);

	const selectorPos = React.useMemo(
		() => getSelectorPosition(displayAngle, selection, is24hour, isPm),
		[displayAngle, selection, is24hour, isPm],
	);

	const trackEnd = React.useMemo(() => {
		const dx = selectorPos.x - CENTER;
		const dy = selectorPos.y - CENTER;
		const length = Math.hypot(dx, dy);
		if (length === 0) return selectorPos;
		return {
			x: selectorPos.x - (dx / length) * HANDLE_RADIUS,
			y: selectorPos.y - (dy / length) * HANDLE_RADIUS,
		};
	}, [selectorPos]);

	const numbers = React.useMemo((): ClockNumber[] => {
		if (selection === "minute") return buildMinuteNumbers();
		if (is24hour) return buildHour24Numbers();
		return buildHour12Numbers();
	}, [selection, is24hour]);

	const isDragging = React.useRef(false);
	const svgRef = React.useRef<SVGSVGElement>(null);

	/** Convert pointer event → SVG coordinate */
	const getSVGPoint = (
		e: PointerEvent | React.PointerEvent,
	): { x: number; y: number } => {
		const svg = svgRef.current;
		if (!svg) return { x: e.clientX, y: e.clientY };
		const rect = svg.getBoundingClientRect();
		const scaleX = TP_SIZE.clockDialSize / rect.width;
		const scaleY = TP_SIZE.clockDialSize / rect.height;
		return {
			x: (e.clientX - rect.left) * scaleX,
			y: (e.clientY - rect.top) * scaleY,
		};
	};

	/** Handles new value from pointer position */
	const handlePosition = (x: number, y: number, isPointerUp = false): void => {
		const angle = getAngleFromPointer(x, y);

		if (selection === "hour") {
			let newHour: number;

			if (is24hour) {
				// 24h: check distance from center to differentiate inner (PM) / outer (AM)
				const dist = Math.sqrt((x - CENTER) ** 2 + (y - CENTER) ** 2);
				const isPmHour = dist < (OUTER_RADIUS + INNER_RADIUS) / 2;
				const hour12 = angleToHour12(angle);
				newHour = isPmHour ? hour12 + 12 : hour12 === 12 ? 0 : hour12;
			} else {
				const hour12 = angleToHour12(angle);
				// Convert 12h display → 24h internal
				newHour = hour12 === 12 ? (isPm ? 12 : 0) : isPm ? hour12 + 12 : hour12;
			}

			setHour(newHour);

			// Auto-switch: switch to minute after pointer up
			if (isPointerUp && autoSwitchToMinute) {
				setTimeout(() => setSelection("minute"), 100);
			}
		} else {
			const newMinute = angleToMinute(angle, !isDragging.current);
			setMinute(newMinute);
		}
	};

	const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>): void => {
		e.currentTarget.setPointerCapture(e.pointerId);
		isDragging.current = false;
		const { x, y } = getSVGPoint(e);
		handlePosition(x, y);
	};

	const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>): void => {
		if (e.buttons === 0) return; // mouse not pressed
		isDragging.current = true;
		const { x, y } = getSVGPoint(e);
		handlePosition(x, y);
	};

	const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>): void => {
		e.currentTarget.releasePointerCapture(e.pointerId);
		const { x, y } = getSVGPoint(e);
		handlePosition(x, y, true);
		isDragging.current = false;
	};

	const getDisplayHour = (): number => {
		if (is24hour) return hour;
		return hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
	};

	/**
	 * MD3 spec: when the handle circle overlaps a number, the number switches to OnPrimary.
	 * For minutes, we use geometric distance to avoid nearest-5 rounding causing abrupt jumps.
	 */
	const isNumberHighlighted = (num: ClockNumber): boolean => {
		if (selection === "hour") return num.value === getDisplayHour();
		const dx = selectorPos.x - num.x;
		const dy = selectorPos.y - num.y;
		return Math.sqrt(dx * dx + dy * dy) < HANDLE_RADIUS;
	};

	/**
	 * Hour numbers move to sit inside the handle circle.
	 * Minute numbers always stay at their fixed positions — the handle naturally overlaps them.
	 */
	const isNumberAtHandle = (num: ClockNumber): boolean => {
		if (selection === "hour") return num.value === getDisplayHour();
		return false;
	};

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<svg
			ref={svgRef}
			width={TP_SIZE.clockDialSize}
			height={TP_SIZE.clockDialSize}
			viewBox={`0 0 ${TP_SIZE.clockDialSize} ${TP_SIZE.clockDialSize}`}
			role="img"
			aria-label={`Clock face, ${selection === "hour" ? "select hour" : "select minute"}`}
			style={style}
			overflow="visible"
			className={cn("touch-none select-none cursor-pointer", className)}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
		>
			{/* ── Background circle ── */}
			<circle cx={CENTER} cy={CENTER} r={CENTER} fill={TP_COLORS.clockDial} />

			{/* ── Selector track (line from center → handle) ── */}
			<line
				x1={CENTER}
				y1={CENTER}
				x2={trackEnd.x}
				y2={trackEnd.y}
				stroke={TP_COLORS.selectorTrack}
				strokeWidth={TP_SIZE.selectorTrackWidth}
				strokeLinecap="round"
			/>

			{/* ── Handle circle ── */}
			<circle
				cx={selectorPos.x}
				cy={selectorPos.y}
				r={HANDLE_RADIUS}
				fill={TP_COLORS.selectorHandle}
			/>

			{/* ── Center dot — rendered after handle to stay on top ── */}
			<circle
				cx={CENTER}
				cy={CENTER}
				r={TP_SIZE.selectorCenterSize / 2}
				fill={TP_COLORS.selectorCenter}
			/>

			{/* ── Number labels — rendered last on top of handle ── */}
			{numbers.map((num) => {
				const highlighted = isNumberHighlighted(num);
				const atHandle = isNumberAtHandle(num);
				// Only move into handle when exactly matching selected tick

				return (
					<text
						key={`${num.value}-${num.radius}`}
						x={atHandle ? selectorPos.x : num.x}
						y={atHandle ? selectorPos.y : num.y}
						textAnchor="middle"
						dominantBaseline="central"
						fontSize={is24hour && num.radius === INNER_RADIUS ? 12 : 14}
						fontWeight={highlighted ? "600" : "400"}
						fill={
							highlighted
								? TP_COLORS.clockDialSelected
								: TP_COLORS.clockDialUnselected
						}
						aria-label={String(num.value)}
						className="pointer-events-none"
					>
						{selection === "hour" && is24hour
							? String(num.value).padStart(2, "0")
							: num.value}
					</text>
				);
			})}
		</svg>
	);
};

ClockDial.displayName = "ClockDial";
